import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Calendar as CalendarIcon, Plus, Trash2, MapPin, Clock, ChevronLeft, ChevronRight, Repeat, Video, Users, Bell, ListChecks, Link2, X } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const PRIVACY_LABELS = { workspace: "Everyone in workspace", department: "Department only", private: "Private + members" };

export default function Calendar() {
  const { activeWorkspaceId, user } = useAuth();
  const [calendars, setCalendars] = useState([]);
  const [events, setEvents] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(true);
  const [activeCals, setActiveCals] = useState(new Set());
  const [editingCal, setEditingCal] = useState(null);
  const [editingEvt, setEditingEvt] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("acquilix.calendar.view") || "month"); // month | week | day | schedule
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });

  // For each view, compute the fetch range and the "header month/date"
  const { fromIso, toIso, month } = useMemo(() => {
    if (view === "month") {
      const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const from = new Date(first.getFullYear(), first.getMonth(), -6);
      const to = new Date(first.getFullYear(), first.getMonth() + 1, 13);
      return { fromIso: from.toISOString(), toIso: to.toISOString(), month: first };
    }
    if (view === "week") {
      const start = startOfWeek(anchor);
      const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7);
      return { fromIso: start.toISOString(), toIso: end.toISOString(), month: anchor };
    }
    if (view === "day") {
      const start = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
      const end = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() + 1);
      return { fromIso: start.toISOString(), toIso: end.toISOString(), month: anchor };
    }
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 30);
    return { fromIso: start.toISOString(), toIso: end.toISOString(), month: anchor };
  }, [anchor, view]);
  useEffect(() => { localStorage.setItem("acquilix.calendar.view", view); }, [view]);

  const step = (dir) => {
    const d = new Date(anchor);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else if (view === "week") d.setDate(d.getDate() + dir * 7);
    else if (view === "day") d.setDate(d.getDate() + dir);
    else /* schedule */ d.setDate(d.getDate() + dir * 30);
    setAnchor(d);
  };
  const goToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); setAnchor(d); };

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const [c, e, d, m] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/calendars`),
        api.get(`/workspaces/${activeWorkspaceId}/calendar-events?from_=${fromIso}&to=${toIso}`),
        api.get(`/workspaces/${activeWorkspaceId}/departments`).catch(() => ({ data: [] })),
        api.get(`/workspaces/${activeWorkspaceId}/members`).catch(() => ({ data: [] })),
      ]);
      setCalendars(c.data || []);
      setEvents(e.data || []);
      setDepartments(d.data || []);
      setMembers(m.data || []);
      if (activeCals.size === 0 && (c.data || []).length) {
        setActiveCals(new Set(c.data.map((x) => x.id)));
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId, fromIso, toIso]);

  const toggleCal = (id) => {
    const next = new Set(activeCals);
    next.has(id) ? next.delete(id) : next.add(id);
    setActiveCals(next);
  };

  const visibleEvents = events.filter((e) => activeCals.has(e.calendar_id));
  const eventsByDay = useMemo(() => {
    // Group by LOCAL calendar date so timezone-shifted events land on the
    // correct day for month/schedule grids.
    const pad = (n) => n.toString().padStart(2, "0");
    const map = {};
    visibleEvents.forEach((e) => {
      if (!e.start_at) return;
      const d = new Date(e.start_at);
      if (isNaN(d)) return;
      const day = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      (map[day] = map[day] || []).push(e);
    });
    return map;
  }, [visibleEvents]);

  return (
    <>
      <PageHeader
        eyebrow="Workspace"
        title="Calendar"
        description="Department, team, and private calendars in one timeline. Toggle individual calendars on/off in the sidebar."
        testid="calendar-header"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => setEditingCal({})} data-testid="calendar-new-cal-btn"><Plus className="w-4 h-4 mr-2" /> New calendar</Button>
            <Button className="rounded-none" onClick={() => setEditingEvt({})} disabled={calendars.length === 0} data-testid="calendar-new-event-btn"><Plus className="w-4 h-4 mr-2" /> New event</Button>
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 pb-10 grid grid-cols-12 gap-4">
        {/* Sidebar */}
        <aside className="col-span-12 lg:col-span-3 space-y-3">
          <div className="border border-border" data-testid="calendar-sidebar">
            <div className="px-3 py-2 border-b border-border label-eyebrow bg-muted/40">My calendars</div>
            {calendars.length === 0 ? <div className="px-3 py-4 text-xs text-muted-foreground">No calendars yet. Click &quot;New calendar&quot;.</div>
              : calendars.map((c) => (
                <div key={c.id} className="px-3 py-2 border-b border-border flex items-center gap-2" data-testid={`calendar-item-${c.id}`}>
                  <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                    <input type="checkbox" checked={activeCals.has(c.id)} onChange={() => toggleCal(c.id)} data-testid={`calendar-toggle-${c.id}`} />
                    <span className="w-3 h-3 inline-block" style={{ backgroundColor: c.color }} />
                    <span className="truncate text-sm">{c.name}</span>
                  </label>
                  <button onClick={() => setEditingCal(c)} className="text-[10px] text-muted-foreground hover:text-foreground" data-testid={`calendar-edit-${c.id}`}>edit</button>
                </div>
              ))}
          </div>
        </aside>

        {/* Main */}
        <main className="col-span-12 lg:col-span-9 space-y-3">
          <div className="flex items-center gap-3 flex-wrap" data-testid="calendar-month-nav">
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => step(-1)} data-testid="calendar-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <h2 className="text-2xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>{formatHeader(view, anchor, month)}</h2>
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => step(1)} data-testid="calendar-next"><ChevronRight className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" className="rounded-none" onClick={goToday} data-testid="calendar-today">Today</Button>
            <div className="ml-auto inline-flex border border-border rounded-none" data-testid="calendar-view-switch">
              {[
                { k: "month", label: "Month" },
                { k: "week", label: "Week" },
                { k: "day", label: "Day" },
                { k: "schedule", label: "Schedule" },
              ].map((v) => (
                <button
                  key={v.k}
                  onClick={() => setView(v.k)}
                  className={`px-3 py-1 text-xs font-semibold border-r border-border last:border-r-0 ${view === v.k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                  data-testid={`calendar-view-${v.k}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {busy && events.length === 0 ? <div className="text-sm">Loading…</div>
            : calendars.length === 0 ? <EmptyState title="Create your first calendar" description="Calendars can be visible to the whole workspace, a department, or kept private with explicit members." icon={CalendarIcon} testid="calendar-empty" action={<Button className="rounded-none" onClick={() => setEditingCal({})} data-testid="calendar-empty-cta">New calendar</Button>} />
            : view === "month" ? (
              <MonthGrid month={month} eventsByDay={eventsByDay} onEventClick={setEditingEvt} />
            ) : view === "week" ? (
              <WeekView anchor={anchor} events={visibleEvents} onEventClick={setEditingEvt} />
            ) : view === "day" ? (
              <DayView anchor={anchor} events={visibleEvents} onEventClick={setEditingEvt} />
            ) : (
              <ScheduleView anchor={anchor} events={visibleEvents} onEventClick={setEditingEvt} />
            )}
        </main>
      </div>

      {editingCal && <CalendarDialog wsId={activeWorkspaceId} cal={editingCal} departments={departments} onClose={() => setEditingCal(null)} onSaved={async () => { await load(); setEditingCal(null); }} />}
      {editingEvt && <EventDialog wsId={activeWorkspaceId} evt={editingEvt} calendars={calendars} members={members} me={user} onClose={() => setEditingEvt(null)} onSaved={async () => { await load(); setEditingEvt(null); }} />}
    </>
  );
}

function CalendarDialog({ wsId, cal, departments, onClose, onSaved }) {
  const isNew = !cal?.id;
  const [form, setForm] = useState({
    name: cal.name || "", color: cal.color || "#7c3aed", privacy: cal.privacy || "workspace",
    department_id: cal.department_id || "", description: cal.description || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name required"); return; }
    if (form.privacy === "department" && !form.department_id) { toast.error("Pick a department"); return; }
    setBusy(true);
    try {
      const payload = { ...form, department_id: form.privacy === "department" ? form.department_id : null };
      if (isNew) await api.post(`/workspaces/${wsId}/calendars`, payload);
      else await api.patch(`/calendars/${cal.id}`, payload);
      toast.success("Saved");
      await onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!confirm(`Delete calendar "${cal.name}"?`)) return;
    try { await api.delete(`/calendars/${cal.id}`); toast.success("Deleted"); await onSaved?.(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="calendar-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New calendar" : `Edit · ${cal.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Name *" value={form.name} onChange={(v) => set("name", v)} testid="cal-name" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Color</Label>
              <Input type="color" value={form.color} onChange={(e) => set("color", e.target.value)} className="h-10 w-20" data-testid="cal-color" />
            </div>
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Privacy</Label>
              <Select value={form.privacy} onValueChange={(v) => set("privacy", v)}>
                <SelectTrigger className="h-10" data-testid="cal-privacy"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRIVACY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {form.privacy === "department" && (
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Department *</Label>
              <Select value={form.department_id} onValueChange={(v) => set("department_id", v)}>
                <SelectTrigger className="h-10" data-testid="cal-department"><SelectValue placeholder="Pick…" /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Description</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="cal-description" />
          </div>
        </div>
        <DialogFooter>
          {!isNew && <Button variant="destructive" className="rounded-none mr-auto" onClick={del} data-testid="cal-delete"><Trash2 className="w-4 h-4 mr-2" /> Delete</Button>}
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="cal-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MODE_LABELS = { in_person: "In person", online: "Online", hybrid: "Hybrid" };
const FREQ_LABELS = { none: "Does not repeat", daily: "Daily", weekdays: "Every weekday (Mon–Fri)", weekends: "Weekends (Sat–Sun)", weekly: "Weekly", monthly: "Monthly", yearly: "Yearly" };
const WEEKDAY_CHIPS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // index = python weekday (0=Mon)
const CHANNEL_LABELS = { inapp: "In-app", email: "Email", whatsapp: "WhatsApp" };
const REMINDER_OPTIONS = [
  { mins: 5, label: "5 min" }, { mins: 15, label: "15 min" }, { mins: 30, label: "30 min" },
  { mins: 60, label: "1 hour" }, { mins: 1440, label: "1 day" },
];
const RSVP_META = { yes: { label: "Yes", cls: "bg-emerald-600 text-white" }, maybe: { label: "Maybe", cls: "bg-amber-500 text-white" }, no: { label: "No", cls: "bg-red-600 text-white" } };

function AddToCalendarBar({ eventId, title }) {
  const [links, setLinks] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/events/${eventId}/add-to-calendar-links`);
        if (!cancelled) setLinks(data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [eventId]);
  if (!links) return null;
  const downloadIcs = async () => {
    try {
      const res = await api.get(`/events/${eventId}/invite.ics`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(title || "event").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40)}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download .ics");
    }
  };
  return (
    <div className="border border-border bg-muted/20 p-3 flex flex-wrap items-center gap-2" data-testid="evt-add-to-calendar">
      <span className="text-xs uppercase tracking-widest text-muted-foreground mr-1">Add to calendar:</span>
      <a href={links.google} target="_blank" rel="noreferrer"
         className="inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-semibold border border-border bg-background hover:bg-muted rounded-none"
         data-testid="evt-add-gcal">Google</a>
      <a href={links.outlook} target="_blank" rel="noreferrer"
         className="inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-semibold border border-border bg-background hover:bg-muted rounded-none"
         data-testid="evt-add-outlook">Outlook</a>
      <button type="button" onClick={downloadIcs}
              className="inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-semibold border border-border bg-background hover:bg-muted rounded-none"
              data-testid="evt-add-ics">.ics (Apple / other)</button>
    </div>
  );
}


function EventDialog({ wsId, evt, calendars, members, me, onClose, onSaved }) {
  const isNew = !evt?.id;
  const [form, setForm] = useState({
    calendar_id: evt.calendar_id || calendars[0]?.id || "",
    title: evt.title || "",
    description: evt.description || "",
    start_at: evt.start_at?.slice(0, 16) || new Date().toISOString().slice(0, 16),
    end_at: evt.end_at?.slice(0, 16) || "",
    all_day: !!evt.all_day,
    location: evt.location || "",
    mode: evt.mode || "in_person",
    meeting_link: evt.meeting_link || "",
    agenda: evt.agenda || [],
    reminders: evt.reminders || [],
    reminder_channels: evt.reminder_channels || ["inapp"],
    attendees: evt.attendees || [],
    freq: evt.recurrence?.freq || "none",
    interval: evt.recurrence?.interval || 1,
    until: evt.recurrence?.until?.slice(0, 10) || "",
    count: evt.recurrence?.count || "",
    byWeekday: evt.recurrence?.by_weekday || [],
    ends: evt.recurrence?.count ? "count" : evt.recurrence?.until ? "until" : "never",
  });
  const [agendaDraft, setAgendaDraft] = useState("");
  const [rsvps, setRsvps] = useState(evt.rsvps || {});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const memberName = (uid) => {
    const m = members.find((x) => x.user_id === uid);
    return m?.user?.name || m?.user?.email || uid;
  };
  const myRsvp = rsvps?.[me?.id]?.status;
  const canRsvp = !isNew && ((evt.attendees || []).includes(me?.id) || evt.created_by === me?.id);

  const addAgenda = () => {
    const v = agendaDraft.trim();
    if (!v) return;
    set("agenda", [...form.agenda, v]);
    setAgendaDraft("");
  };
  const toggleReminder = (mins) => {
    set("reminders", form.reminders.includes(mins) ? form.reminders.filter((m) => m !== mins) : [...form.reminders, mins].sort((a, b) => a - b));
  };
  const toggleChannel = (ch) => {
    const next = form.reminder_channels.includes(ch) ? form.reminder_channels.filter((c) => c !== ch) : [...form.reminder_channels, ch];
    set("reminder_channels", next.length ? next : ["inapp"]);
  };
  const toggleWeekday = (d) => {
    set("byWeekday", form.byWeekday.includes(d) ? form.byWeekday.filter((x) => x !== d) : [...form.byWeekday, d].sort());
  };
  const toggleAttendee = (uid) => {
    set("attendees", form.attendees.includes(uid) ? form.attendees.filter((a) => a !== uid) : [...form.attendees, uid]);
  };

  const doRsvp = async (status) => {
    try {
      const r = await api.post(`/events/${evt.id}/rsvp`, { status });
      setRsvps(r.data.rsvps || {});
      toast.success(`RSVP: ${RSVP_META[status].label}`);
    } catch (e) { toast.error(apiErrorMessage(e, "RSVP failed")); }
  };

  const save = async () => {
    if (!form.title?.trim()) { toast.error("Title required"); return; }
    if (!form.calendar_id) { toast.error("Pick a calendar"); return; }
    if (form.mode !== "in_person" && !form.meeting_link.trim()) { toast.error("Meeting link is required for online/hybrid events"); return; }
    setBusy(true);
    try {
      const recurrence = form.freq !== "none"
        ? {
            freq: form.freq,
            interval: Math.max(1, parseInt(form.interval, 10) || 1),
            until: form.ends === "until" ? (form.until || null) : null,
            count: form.ends === "count" ? (parseInt(form.count, 10) || null) : null,
            by_weekday: form.freq === "weekly" && form.byWeekday.length ? form.byWeekday : null,
          }
        : null;
      const payload = {
        title: form.title, description: form.description || null,
        start_at: new Date(form.start_at).toISOString(),
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
        all_day: form.all_day,
        location: form.location || null,
        mode: form.mode,
        meeting_link: form.meeting_link || null,
        agenda: form.agenda,
        reminders: form.reminders,
        reminder_channels: form.reminder_channels,
        attendees: form.attendees,
        recurrence,
      };
      if (isNew) await api.post(`/calendars/${form.calendar_id}/events`, payload);
      else {
        if (!recurrence && evt.recurrence?.freq) { delete payload.recurrence; payload.clear_recurrence = true; }
        await api.patch(`/events/${evt.id}`, payload);
      }
      toast.success("Saved");
      await onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!confirm(`Delete event "${evt.title}"?${evt.recurrence?.freq ? " This deletes the entire recurring series." : ""}`)) return;
    try { await api.delete(`/events/${evt.id}`); toast.success("Deleted"); await onSaved?.(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New event" : `Edit · ${evt.title}`}</DialogTitle></DialogHeader>

        {canRsvp && (
          <div className="border border-border bg-muted/30 p-3 flex items-center gap-2 flex-wrap" data-testid="evt-rsvp-bar">
            <span className="text-sm font-semibold mr-1">Your RSVP:</span>
            {Object.entries(RSVP_META).map(([k, meta]) => (
              <Button key={k} size="sm" variant={myRsvp === k ? "default" : "outline"}
                className={`rounded-none h-7 ${myRsvp === k ? meta.cls : ""}`}
                onClick={() => doRsvp(k)} data-testid={`evt-rsvp-${k}`}>{meta.label}</Button>
            ))}
          </div>
        )}

        {!isNew && evt.id && <AddToCalendarBar eventId={evt.id} title={evt.title} />}

        <div className="space-y-3">
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Calendar *</Label>
            <Select value={form.calendar_id} onValueChange={(v) => set("calendar_id", v)} disabled={!isNew}>
              <SelectTrigger className="h-10" data-testid="evt-calendar"><SelectValue placeholder="Pick…" /></SelectTrigger>
              <SelectContent>{calendars.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Field label="Title *" value={form.title} onChange={(v) => set("title", v)} testid="evt-title" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Starts *" type="datetime-local" value={form.start_at} onChange={(v) => set("start_at", v)} testid="evt-start" />
            <Field label="Ends" type="datetime-local" value={form.end_at} onChange={(v) => set("end_at", v)} testid="evt-end" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.all_day} onChange={(e) => set("all_day", e.target.checked)} data-testid="evt-all-day" /> All day
          </label>

          {/* Recurrence */}
          <div className="border border-border p-3 space-y-2" data-testid="evt-recurrence-section">
            <div className="flex items-center gap-2 text-sm font-semibold"><Repeat className="w-4 h-4" /> Repeat</div>
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.freq} onValueChange={(v) => set("freq", v)}>
                <SelectTrigger className="h-9" data-testid="evt-recur-freq"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(FREQ_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
              {["daily", "weekly", "monthly", "yearly"].includes(form.freq) && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground shrink-0">Every</span>
                  <Input type="number" min={1} max={30} value={form.interval} onChange={(e) => set("interval", e.target.value)} className="h-9 w-20" data-testid="evt-recur-interval" />
                  <span className="text-xs text-muted-foreground shrink-0">{{ daily: "day(s)", weekly: "week(s)", monthly: "month(s)", yearly: "year(s)" }[form.freq]}</span>
                </div>
              )}
            </div>
            {form.freq === "weekly" && (
              <div className="flex gap-1.5 flex-wrap" data-testid="evt-recur-weekdays">
                {WEEKDAY_CHIPS.map((d, i) => (
                  <button key={d} onClick={() => toggleWeekday(i)}
                    className={`text-xs w-10 py-1 border ${form.byWeekday.includes(i) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
                    data-testid={`evt-weekday-${i}`}>{d}</button>
                ))}
                <span className="text-[10px] text-muted-foreground self-center ml-1">{form.byWeekday.length === 0 ? "No days picked — repeats on the start day" : ""}</span>
              </div>
            )}
            {form.freq !== "none" && (
              <div className="grid grid-cols-[110px_1fr] gap-3 items-center">
                <Select value={form.ends} onValueChange={(v) => set("ends", v)}>
                  <SelectTrigger className="h-9" data-testid="evt-recur-ends"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">Never ends</SelectItem>
                    <SelectItem value="until">On date</SelectItem>
                    <SelectItem value="count">After N times</SelectItem>
                  </SelectContent>
                </Select>
                {form.ends === "until" && <Input type="date" value={form.until} onChange={(e) => set("until", e.target.value)} className="h-9" data-testid="evt-recur-until" />}
                {form.ends === "count" && (
                  <div className="flex items-center gap-1.5">
                    <Input type="number" min={1} max={730} value={form.count} onChange={(e) => set("count", e.target.value)} placeholder="10" className="h-9 w-24" data-testid="evt-recur-count" />
                    <span className="text-xs text-muted-foreground">occurrence(s)</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mode + location + link */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Mode</Label>
              <Select value={form.mode} onValueChange={(v) => set("mode", v)}>
                <SelectTrigger className="h-10" data-testid="evt-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(MODE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.mode === "online" ? (
              <div>
                <Label className="mb-1.5 text-sm font-semibold flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Meeting link</Label>
                <Input value={form.meeting_link} onChange={(e) => set("meeting_link", e.target.value)} placeholder="https://meet.google.com/…" className="h-10" data-testid="evt-meeting-link" />
              </div>
            ) : (
              <Field label="Location" value={form.location} onChange={(v) => set("location", v)} testid="evt-location" />
            )}
          </div>
          {form.mode === "hybrid" && (
            <div>
              <Label className="mb-1.5 text-sm font-semibold flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Meeting link</Label>
              <Input value={form.meeting_link} onChange={(e) => set("meeting_link", e.target.value)} placeholder="https://meet.google.com/…" className="h-10" data-testid="evt-meeting-link" />
            </div>
          )}

          {/* Agenda */}
          <div className="border border-border p-3 space-y-2" data-testid="evt-agenda-section">
            <div className="flex items-center gap-2 text-sm font-semibold"><ListChecks className="w-4 h-4" /> Agenda</div>
            {form.agenda.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm bg-muted/40 px-2 py-1" data-testid={`evt-agenda-item-${i}`}>
                <span className="font-mono text-xs text-muted-foreground">{i + 1}.</span>
                <span className="flex-1 min-w-0 truncate">{item}</span>
                <button onClick={() => set("agenda", form.agenda.filter((_, j) => j !== i))} className="text-destructive" data-testid={`evt-agenda-del-${i}`}><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <div className="flex gap-2">
              <Input value={agendaDraft} onChange={(e) => setAgendaDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAgenda(); } }} placeholder="Add agenda item…" className="h-9" data-testid="evt-agenda-input" />
              <Button variant="outline" size="sm" className="rounded-none h-9" onClick={addAgenda} data-testid="evt-agenda-add"><Plus className="w-3.5 h-3.5" /></Button>
            </div>
          </div>

          {/* Reminders */}
          <div className="border border-border p-3 space-y-2" data-testid="evt-reminders-section">
            <div className="flex items-center gap-2 text-sm font-semibold"><Bell className="w-4 h-4" /> Alerts before start</div>
            <div className="flex gap-2 flex-wrap">
              {REMINDER_OPTIONS.map((o) => (
                <button key={o.mins} onClick={() => toggleReminder(o.mins)}
                  className={`text-xs px-2.5 py-1 border ${form.reminders.includes(o.mins) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
                  data-testid={`evt-reminder-${o.mins}`}>{o.label}</button>
              ))}
            </div>
            {form.reminders.length > 0 && (
              <div className="pt-1 border-t border-border/60">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Alert via</div>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(CHANNEL_LABELS).map(([ch, label]) => (
                    <button key={ch} onClick={() => toggleChannel(ch)}
                      className={`text-xs px-2.5 py-1 border ${form.reminder_channels.includes(ch) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
                      data-testid={`evt-channel-${ch}`}>{label}</button>
                  ))}
                </div>
                {form.reminder_channels.includes("whatsapp") && <p className="text-[10px] text-muted-foreground mt-1.5">WhatsApp uses your Twilio WhatsApp integration (Marketing → Providers) and the attendee&apos;s employee phone number.</p>}
                {form.reminder_channels.includes("email") && <p className="text-[10px] text-muted-foreground mt-1.5">Email is sent to each attendee&apos;s account email.</p>}
              </div>
            )}
          </div>

          {/* Attendees */}
          <div className="border border-border p-3 space-y-2" data-testid="evt-attendees-section">
            <div className="flex items-center gap-2 text-sm font-semibold"><Users className="w-4 h-4" /> Attendees {form.attendees.length > 0 && <span className="text-xs text-muted-foreground font-normal">({form.attendees.length})</span>}</div>
            <div className="max-h-36 overflow-y-auto space-y-1">
              {members.filter((m) => m.user_id !== me?.id).map((m) => {
                const rs = rsvps?.[m.user_id]?.status;
                return (
                  <label key={m.user_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/40 px-1.5 py-1">
                    <input type="checkbox" checked={form.attendees.includes(m.user_id)} onChange={() => toggleAttendee(m.user_id)} data-testid={`evt-attendee-${m.user_id}`} />
                    <span className="flex-1 truncate">{m.user?.name || m.user?.email}</span>
                    {rs && <span className={`text-[10px] px-1.5 py-0.5 ${RSVP_META[rs].cls}`} data-testid={`evt-attendee-rsvp-${m.user_id}`}>{RSVP_META[rs].label}</span>}
                  </label>
                );
              })}
              {members.filter((m) => m.user_id !== me?.id).length === 0 && <div className="text-xs text-muted-foreground px-1.5">No other workspace members.</div>}
            </div>
          </div>

          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Description</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="evt-description" />
          </div>
        </div>
        <DialogFooter>
          {!isNew && <Button variant="destructive" className="rounded-none mr-auto" onClick={del} data-testid="evt-delete"><Trash2 className="w-4 h-4 mr-2" /> Delete</Button>}
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="evt-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-10" data-testid={testid} />
    </div>
  );
}

// ───────────────────────── VIEW HELPERS & COMPONENTS ─────────────────────────

function startOfWeek(d) {
  // Sunday-start week (matches Month grid)
  const day = d.getDay();
  const s = new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
  s.setHours(0, 0, 0, 0);
  return s;
}

function formatHeader(view, anchor, month) {
  if (view === "month") return month.toLocaleString("default", { month: "long", year: "numeric" });
  if (view === "week") {
    const s = startOfWeek(anchor);
    const e = new Date(s.getFullYear(), s.getMonth(), s.getDate() + 6);
    const opts = { month: "short", day: "numeric" };
    return `${s.toLocaleDateString("default", opts)} – ${e.toLocaleDateString("default", opts)}, ${s.getFullYear()}`;
  }
  if (view === "day") return anchor.toLocaleDateString("default", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  return `Next 30 days · from ${anchor.toLocaleDateString("default", { month: "short", day: "numeric" })}`;
}

function eventShortTime(e) {
  if (e.all_day) return "All day";
  return (e.start_at || "").slice(11, 16);
}

function MonthGrid({ month, eventsByDay, onEventClick }) {
  const grid = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const startWeekday = first.getDay();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    for (let d = 1; d <= last; d++) {
      const date = new Date(month.getFullYear(), month.getMonth(), d);
      const iso = date.toISOString().slice(0, 10);
      cells.push({ date, iso, events: eventsByDay[iso] || [] });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month, eventsByDay]);

  return (
    <div className="border border-border" data-testid="calendar-grid">
      <div className="grid grid-cols-7 border-b border-border bg-muted/40">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="px-2 py-1 label-eyebrow text-[10px] text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {grid.map((cell, idx) => (
          <div key={idx} className={`min-h-[100px] border-r border-b border-border p-1.5 ${!cell ? "bg-muted/20" : ""}`} data-testid={cell ? `calendar-day-${cell.iso}` : undefined}>
            {cell && (
              <>
                <div className="text-xs font-mono mb-1">{cell.date.getDate()}</div>
                <div className="space-y-1">
                  {cell.events.slice(0, 3).map((e) => (
                    <button
                      key={e.occurrence_id || e.id}
                      onClick={() => onEventClick(e)}
                      className="w-full text-left text-[10px] px-1 py-0.5 truncate text-white flex items-center gap-1"
                      style={{ backgroundColor: e.calendar_color || "#7c3aed" }}
                      title={`${e.title} · ${e.calendar_name}`}
                      data-testid={`event-${e.id}`}
                    >
                      {e.recurrence?.freq && <Repeat className="w-2.5 h-2.5 shrink-0" />}
                      {e.mode === "online" && <Video className="w-2.5 h-2.5 shrink-0" />}
                      <span className="truncate">{e.all_day ? "" : `${eventShortTime(e)} `}{e.title}</span>
                    </button>
                  ))}
                  {cell.events.length > 3 && <div className="text-[10px] text-muted-foreground">+{cell.events.length - 3} more</div>}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function WeekView({ anchor, events, onEventClick }) {
  const pad = (n) => n.toString().padStart(2, "0");
  const localIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const eventLocalIso = (e) => {
    const d = new Date(e.start_at);
    return isNaN(d) ? "" : localIso(d);
  };
  const start = startOfWeek(anchor);
  const days = [];
  const todayIso = localIso(new Date());
  for (let i = 0; i < 7; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const iso = localIso(d);
    days.push({
      date: d,
      iso,
      isToday: iso === todayIso,
      events: events.filter((e) => eventLocalIso(e) === iso).sort((a, b) => (a.start_at || "").localeCompare(b.start_at || "")),
    });
  }
  return (
    <div className="border border-border grid grid-cols-7 divide-x divide-border" data-testid="calendar-week-grid">
      {days.map((d) => (
        <div key={d.iso} className={`min-h-[280px] ${d.isToday ? "bg-primary/5" : ""}`} data-testid={`calendar-week-day-${d.iso}`}>
          <div className="px-2 py-1.5 border-b border-border bg-muted/40 text-center">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{d.date.toLocaleDateString("default", { weekday: "short" })}</div>
            <div className={`text-lg font-bold ${d.isToday ? "text-primary" : ""}`}>{d.date.getDate()}</div>
          </div>
          <div className="p-1.5 space-y-1">
            {d.events.length === 0 && <div className="text-[10px] text-muted-foreground italic px-1">No events</div>}
            {d.events.map((e) => (
              <button
                key={e.occurrence_id || e.id}
                onClick={() => onEventClick(e)}
                className="w-full text-left text-[11px] px-1.5 py-1 text-white flex flex-col gap-0.5"
                style={{ backgroundColor: e.calendar_color || "#7c3aed" }}
                data-testid={`event-${e.id}`}
              >
                <div className="font-mono text-[9px] opacity-80">{eventShortTime(e)}</div>
                <div className="truncate flex items-center gap-1">
                  {e.mode === "online" && <Video className="w-2.5 h-2.5 shrink-0" />}
                  <span className="truncate">{e.title}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function DayView({ anchor, events, onEventClick }) {
  // Use the LOCAL date components (not UTC) so events created "today" in the
  // user's timezone show up on today's Day view even when their UTC ISO date
  // falls on a different calendar day.
  const pad = (n) => n.toString().padStart(2, "0");
  const localIso = `${anchor.getFullYear()}-${pad(anchor.getMonth() + 1)}-${pad(anchor.getDate())}`;
  const evts = events.filter((e) => {
    if (!e.start_at) return false;
    const d = new Date(e.start_at);
    if (isNaN(d)) return false;
    const evtLocal = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    return evtLocal === localIso;
  }).sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""));
  const hours = Array.from({ length: 15 }, (_, i) => i + 7); // 7 AM → 9 PM
  return (
    <div className="border border-border" data-testid="calendar-day-view">
      <div className="border-b border-border bg-muted/40 px-3 py-2">
        <div className="text-lg font-bold">{anchor.toLocaleDateString("default", { weekday: "long", month: "long", day: "numeric" })}</div>
        <div className="text-xs text-muted-foreground">{evts.length} event{evts.length === 1 ? "" : "s"}</div>
      </div>
      {evts.length === 0 ? (
        <div className="p-6 text-sm text-muted-foreground text-center" data-testid="calendar-day-empty">Nothing scheduled today.</div>
      ) : (
        <div className="divide-y divide-border">
          {hours.map((h) => {
            const hourEvts = evts.filter((e) => {
              if (e.all_day) return h === 7;
              // Bucket by LOCAL hour, not UTC hour
              return new Date(e.start_at).getHours() === h;
            });
            return (
              <div key={h} className="grid grid-cols-[60px_1fr] items-start px-3 py-1.5 min-h-[48px]">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground pt-1">
                  {h.toString().padStart(2, "0")}:00
                </div>
                <div className="space-y-1">
                  {hourEvts.map((e) => (
                    <button
                      key={e.occurrence_id || e.id}
                      onClick={() => onEventClick(e)}
                      className="w-full text-left px-2 py-1.5 text-white text-sm"
                      style={{ backgroundColor: e.calendar_color || "#7c3aed" }}
                      data-testid={`event-${e.id}`}
                    >
                      <div className="font-mono text-[10px] opacity-80">{e.all_day ? "All day" : `${eventShortTime(e)}${e.end_at ? ` – ${(e.end_at || "").slice(11, 16)}` : ""}`}</div>
                      <div className="font-semibold truncate flex items-center gap-1">
                        {e.mode === "online" && <Video className="w-3 h-3 shrink-0" />}
                        {e.title}
                      </div>
                      {e.location && <div className="text-[10px] opacity-90 truncate">{e.location}</div>}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ScheduleView({ anchor, events, onEventClick }) {
  const pad = (n) => n.toString().padStart(2, "0");
  const localIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const eventLocalIso = (e) => {
    const d = new Date(e.start_at);
    return isNaN(d) ? "" : localIso(d);
  };
  const start = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
  const days = [];
  for (let i = 0; i < 30; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const iso = localIso(d);
    const dayEvts = events.filter((e) => eventLocalIso(e) === iso).sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""));
    if (dayEvts.length > 0) days.push({ date: d, iso, events: dayEvts });
  }
  if (days.length === 0) {
    return <div className="border border-border p-8 text-center text-sm text-muted-foreground" data-testid="calendar-schedule-empty">No upcoming events in the next 30 days.</div>;
  }
  return (
    <div className="border border-border divide-y divide-border" data-testid="calendar-schedule-view">
      {days.map((d) => (
        <div key={d.iso} className="grid grid-cols-[110px_1fr] px-3 py-2.5 gap-3" data-testid={`schedule-day-${d.iso}`}>
          <div>
            <div className="text-2xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>{d.date.getDate()}</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{d.date.toLocaleDateString("default", { weekday: "short", month: "short" })}</div>
          </div>
          <div className="space-y-1.5">
            {d.events.map((e) => (
              <button
                key={e.occurrence_id || e.id}
                onClick={() => onEventClick(e)}
                className="w-full text-left px-3 py-2 border border-border hover:border-primary transition flex items-start gap-3"
                data-testid={`event-${e.id}`}
              >
                <div className="w-1 self-stretch shrink-0" style={{ backgroundColor: e.calendar_color || "#7c3aed" }} />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate flex items-center gap-1">
                    {e.mode === "online" && <Video className="w-3.5 h-3.5 shrink-0" />}
                    {e.recurrence?.freq && <Repeat className="w-3 h-3 shrink-0 opacity-60" />}
                    <span className="truncate">{e.title}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {e.all_day ? "All day" : `${eventShortTime(e)}${e.end_at ? ` – ${(e.end_at || "").slice(11, 16)}` : ""}`}
                    {e.location ? ` · ${e.location}` : ""}
                    {e.calendar_name ? ` · ${e.calendar_name}` : ""}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
